import { getLocale, type Locale } from '@/lib/locale';
import { ES_IMAGE_STITCH_DATA } from './image-stitch-seo-data-es';

export const IMAGE_STITCH_PATH = '/image-stitch';
export const IMAGE_STITCH_DATE_PUBLISHED = '2026-09-07';
export const IMAGE_STITCH_LAST_UPDATED = '2026-09-18';
export const IMAGE_STITCH_LAST_UPDATED_LABEL = 'September 18, 2026';

export const IMAGE_STITCH_SEO_TITLE =
  'Image Stitcher Online | Combine 2-9 Photos Locally';
export const IMAGE_STITCH_H1 =
  'Image Stitcher: Combine 2-9 Photos in Horizontal, Vertical, or Grid Layouts';
export const IMAGE_STITCH_DEFINITION_H2 =
  'How does this image stitcher keep sources in separate cells?';
export const IMAGE_STITCH_SEO_DESCRIPTION =
  'An image stitcher combines 2-9 JPG, PNG, or WebP files in your browser into one PNG or JPEG. Choose horizontal, vertical, or grid. Files are kept local.';
export const IMAGE_STITCH_KEYWORDS = [
  'image stitcher',
  'stitch images online',
  'combine photos',
  'merge photos',
  'put two pictures side by side',
  'vertical long image',
  'photo grid',
  'stitch images without uploading',
  'combine images in browser',
  'contact sheet maker',
].join(', ');

export const IMAGE_STITCH_QUICK_ANSWER =
  'An image stitcher combines two to nine JPG, PNG, or WebP files in your browser into one PNG or JPEG file. Choose a horizontal, vertical, or grid layout. The original files are not sent to an upload API.';

export const IMAGE_STITCH_PROOF_IMAGE =
  '/image-stitch/image-stitcher-horizontal-16-9-proof.png';
export const IMAGE_STITCH_PROOF_WIDTH = 1952;
export const IMAGE_STITCH_PROOF_HEIGHT = 360;
export const IMAGE_STITCH_PROOF_CAPTION =
  'This image stitcher export uses three 16:9 source frames in a Horizontal layout, Cover fit, 16px white gap, PNG.';
export const IMAGE_STITCH_PROOF_CANVAS =
  'The example canvas is 1952 x 360 pixels.';
export const IMAGE_STITCH_TYPICAL_TIME =
  'Typical time: under 1 minute after two files are added.';

export const IMAGE_STITCH_STEPS = [
  {
    name: 'Add two to nine images',
    text: 'Choose JPG, PNG, or WebP files up to 20MB each. The image stitcher reads them in your browser, and the numbered source tiles show the order used in the final canvas.',
  },
  {
    name: 'Choose a horizontal, vertical, or grid layout',
    text: 'Use a horizontal row for comparisons, a vertical column for long images, or a grid for a compact gallery. Move any source earlier or later before exporting.',
  },
  {
    name: 'Adjust ratio, fit, gap, and background',
    text: 'Select 1:1, 4:5, or 16:9 cells. Cover fills each cell by cropping edges, while contain keeps the full image and uses the selected background around it.',
  },
  {
    name: 'Preview and download the stitched image',
    text: 'Check the live preview and export the completed canvas as PNG or JPEG. The download is created locally without sending source files to an upload API.',
  },
] as const;

export const IMAGE_STITCH_FEATURES = [
  {
    title: 'Three layout directions',
    body: 'Place images in one horizontal row, one vertical column, or an automatically balanced grid. The image stitcher supports 2-9 cells and recalculates the canvas when the source order changes.',
  },
  {
    title: 'Predictable cell framing',
    body: 'Choose square, 4:5 portrait, or 16:9 landscape cells. Use Contain to retain the full source or Cover to fill each cell with an edge crop.',
  },
  {
    title: 'Gap and canvas control',
    body: 'Set spacing from 0 to 64 pixels and choose any background color. Adjustable spacing and canvas color support clean comparisons, dark mood boards, and tightly joined screenshot strips in the same workflow.',
  },
  {
    title: 'High-resolution exports',
    body: 'Download PNG when screenshots, text, or diagram edges must stay crisp, or JPEG when a smaller photo file matters more. Exports are limited to 8,192 pixels on the longest edge for browser reliability.',
  },
] as const;

export const IMAGE_STITCH_USE_CASES = [
  {
    title: 'Before-and-after comparisons',
    input:
      'Two edits, product versions, or progress photos that need to be judged in one frame.',
    action:
      'In the image stitcher, put them side by side in a Horizontal layout with equal cells and a small gap so viewers can compare the result without opening separate files.',
    result:
      'One wide comparison image that keeps each source in its own panel.',
  },
  {
    title: 'Long screenshot references',
    input:
      'Screenshots, steps, or sequential captures that should be read from top to bottom.',
    action:
      'In the image stitcher, add the files in reading order, choose a Vertical layout and Contain, then set the gap to zero or a narrow divider.',
    result: 'One long image for review, annotation, or documentation.',
  },
  {
    title: 'Mood boards and contact sheets',
    input: 'Four to nine references that should share equal visual weight.',
    action:
      'In the image stitcher, choose a Grid layout and a consistent ratio so the tool produces one compact overview.',
    result:
      'A single gallery file for a creative brief, presentation, or social draft.',
  },
] as const;

export const IMAGE_STITCH_LIMITS = [
  {
    title: 'This is a layout tool, not panorama software',
    body: 'The image stitcher does not align overlapping panorama frames, blend seams, remove backgrounds, add labels, or generate missing content between sources.',
  },
  {
    title: 'Cover can crop important edges',
    body: 'Check Cover crops at faces, text, logos, and the outer edge of products before downloading. Use Contain when the complete source must remain visible.',
  },
  {
    title: 'Very large layouts are scaled',
    body: 'Large nine-image rows or columns are scaled to keep the longest export edge at or below 8,192 pixels. Preview rendering uses a smaller 1,600-pixel canvas.',
  },
  {
    title: 'Choose PNG or JPEG for the job',
    body: 'Use PNG for interface captures, diagrams, and later editing. JPEG uses a 92% quality setting and is better when a smaller photo file matters more.',
  },
  {
    title: 'Downloaded files are not stored in History',
    body: 'All selected images stay in the browser tab during this workflow. The downloaded canvas is not saved to History, so keep the file on your device if you need it later.',
  },
] as const;

export const IMAGE_STITCH_FAQS = [
  {
    question: 'What is an image stitcher?',
    answer:
      'An image stitcher combines multiple image files into one larger canvas. This tool places 2-9 images in a horizontal row, vertical column, or grid. It does not use AI to invent or replace pixels.',
  },
  {
    question: 'Can I stitch images together without uploading them?',
    answer:
      'Yes. The image stitcher reads, arranges, previews, and exports images in the current browser tab. Your selected files are not sent to the site server by this workflow.',
  },
  {
    question: 'How do I put two pictures side by side?',
    answer:
      'Add two pictures, select Horizontal, choose a cell ratio, and set image fit to Cover or Contain. Adjust the gap, verify the preview, then download the two pictures as one PNG or JPEG.',
  },
  {
    question: 'Does image stitching reduce quality?',
    answer:
      'The export uses a high-resolution canvas up to 8,192 pixels on its longest edge. If a calculated layout would exceed that limit, the image stitcher scales the complete canvas down. JPEG uses lossy compression, so choose PNG when text and interface edges must stay crisp.',
  },
  {
    question: 'Is this the same as panorama stitching or AI image combining?',
    answer:
      'No. Panorama software aligns overlapping photographs. An AI image editor can generate a new composition from several references. This image stitcher keeps sources in separate cells and joins those cells on one canvas. Use the AI image editor when you need a newly generated composition.',
  },
] as const;

export const IMAGE_STITCH_LAYOUT_GUIDE = [
  {
    layout: 'Horizontal',
    input: 'Two to five comparisons or story beats',
    output: 'Wide side-by-side image',
    fit: 'Cover for equal panels',
  },
  {
    layout: 'Vertical',
    input: 'Screenshots, steps, or sequential references',
    output: 'Long image read from top to bottom',
    fit: 'Contain for full screenshots',
  },
  {
    layout: 'Grid',
    input: 'Four to nine photos with equal importance',
    output: 'Compact gallery or contact sheet',
    fit: 'Cover for consistent cells',
  },
] as const;

export const IMAGE_STITCH_SPECS = [
  { label: 'Input formats', value: 'JPG, PNG, and WebP' },
  { label: 'Image count', value: '2-9 images' },
  { label: 'File size', value: 'Up to 20MB per image' },
  { label: 'Layouts', value: 'Horizontal, vertical, and grid' },
  { label: 'Cell ratios', value: '1:1, 4:5, and 16:9' },
  { label: 'Gap', value: '0-64 pixels' },
  {
    label: 'Background',
    value: 'Solid color; transparent output is not supported',
  },
  { label: 'Downloads', value: 'PNG or JPEG' },
  {
    label: 'Preview size',
    value: 'Up to 1,600 pixels on the longest edge',
  },
  {
    label: 'Maximum output',
    value: '8,192 pixels on the longest edge',
  },
  { label: 'JPEG quality', value: '92% quality setting' },
  { label: 'Unsupported formats', value: 'GIF, HEIC, and SVG' },
  {
    label: 'Processing',
    value: 'Local browser tab; source files are not sent to an upload API',
  },
  {
    label: 'Browser requirement',
    value: 'JavaScript and HTML canvas support',
  },
] as const;

export const IMAGE_STITCH_SCHEMA_FEATURES = [
  'Horizontal, vertical, and grid image layouts',
  '2-9 JPG, PNG, and WebP sources',
  '1:1, 4:5, and 16:9 cell ratios',
  'Contain and Cover image fit',
  'Local browser preview and PNG or JPEG download',
] as const;

export type ImageStitchSeoData = {
  path: string;
  seoTitle: string;
  h1: string;
  definitionH2: string;
  seoDescription: string;
  keywords: string;
  datePublished: string;
  lastUpdated: string;
  lastUpdatedLabel: string;
  quickAnswer: string;
  proofImage: string;
  proofWidth: number;
  proofHeight: number;
  proofCaption: string;
  proofCanvas: string;
  typicalTime: string;
  steps: readonly { name: string; text: string }[];
  features: readonly { title: string; body: string }[];
  useCases: readonly {
    title: string;
    input: string;
    action: string;
    result: string;
  }[];
  limits: readonly { title: string; body: string }[];
  faqs: readonly { question: string; answer: string }[];
  layoutGuide: readonly {
    layout: string;
    input: string;
    output: string;
    fit: string;
  }[];
  specs: readonly { label: string; value: string }[];
  schemaFeatures: readonly string[];
  copy: ImageStitchCopy;
  ui: ImageStitchUi;
};

export type ImageStitchCopy = {
  relatedTools: {
    heading: string;
    description: string;
    ariaLabel: string;
    items: readonly {
      alt: string;
      detail: string;
      href: string;
      image: string;
      label: string;
    }[];
  };
  breadcrumb: string;
  quickAnswer: {
    eyebrow: string;
    afterAnswer: string;
    publishedPrefix: string;
    facts: readonly { label: string; value: string }[];
  };
  definition: {
    eyebrow: string;
    description: string;
    p1: string;
    p2: string;
  };
  proof: {
    eyebrow: string;
    title: string;
    description: string;
    alt: string;
    figcaptionExtra: string;
  };
  capabilities: {
    eyebrow: string;
    title: string;
    description: string;
  };
  howTo: {
    eyebrow: string;
    title: string;
    description: string;
  };
  layoutGuide: {
    eyebrow: string;
    title: string;
    description: string;
    caption: string;
    headers: {
      layout: string;
      input: string;
      output: string;
      fit: string;
    };
  };
  specs: {
    eyebrow: string;
    title: string;
    description: string;
    caption: string;
    headers: { parameter: string; support: string };
  };
  useCases: {
    eyebrow: string;
    title: string;
    description: string;
    startingInput: string;
    toolAction: string;
    expectedResult: string;
  };
  limits: {
    eyebrow: string;
    title: string;
    description: string;
  };
  faq: {
    eyebrow: string;
    title: string;
    description: string;
  };
  nextSteps: {
    eyebrow: string;
    title: string;
    description: string;
    body: string;
    ariaLabel: string;
    videoLabel: string;
    videoDetail: string;
    privacyLabel: string;
    privacyDetail: string;
  };
  cta: {
    eyebrow: string;
    title: string;
    body: string;
    button: string;
  };
  schema: {
    softwareName: string;
    actionName: string;
    howToName: string;
    howToDescription: string;
    breadcrumbHome: string;
    breadcrumbPage: string;
    proofImageName: string;
  };
};

export type ImageStitchUi = {
  headerTitle: string;
  sourceImages: string;
  addImages: string;
  addHint: string;
  chooseFiles: string;
  layout: string;
  horizontal: string;
  vertical: string;
  grid: string;
  cellRatio: string;
  imageFit: string;
  contain: string;
  cover: string;
  gap: string;
  background: string;
  format: string;
  stitchDownload: string;
  preparingDownload: string;
  preview: string;
  addAtLeastTwo: string;
  noPreview: string;
  noPreviewHint: string;
  waiting: string;
  livePreviewNote: string;
  maxImagesError: string;
  unsupportedTypeError: string;
  fileTooLargeError: string;
  readFailedError: string;
  exportFailedError: string;
  loadFailedError: string;
  previewFailedError: string;
  moveEarlier: string;
  moveLater: string;
  remove: string;
};

export const IMAGE_STITCH_COPY: ImageStitchCopy = {
  relatedTools: {
    heading: 'More image tools',
    description:
      'Continue with an adjacent image workflow. Generate a new composition, edit a source, enlarge the export, or animate the finished still after you arrange it.',
    ariaLabel: 'Continue with an adjacent image workflow',
    items: [
      {
        alt: 'Grok Imagine image generation example poster',
        detail: 'Create a new source image.',
        href: '/image-generator',
        image: '/image-generator/grok-imagine-demo-poster.jpg',
        label: 'Generate an image',
      },
      {
        alt: 'Grok AI image editor collage showing several edited image styles',
        detail: 'Edit a source or the finished canvas.',
        href: '/image-editor',
        image: '/image-editor/grok-image-editor-demo-collage.jpg',
        label: 'Edit an image',
      },
      {
        alt: 'AI image upscaler comparison of a source photo and a larger result',
        detail: 'Increase resolution after export.',
        href: '/image-upscale',
        image: '/image-upscale/old-home-video-first-frame-after.jpg',
        label: 'Upscale an image',
      },
      {
        alt: 'Still image prepared as the start frame for an AI video',
        detail: 'Animate the finished still.',
        href: '/image-to-video',
        image: '/image-to-video/case-studies/start-frame-after.jpg',
        label: 'Turn image into video',
      },
    ],
  },
  breadcrumb: 'Image stitcher',
  quickAnswer: {
    eyebrow: 'Image stitcher - quick answer',
    afterAnswer:
      'Use the image stitcher when you already have finished stills and need one predictable canvas. It is a layout step before Grok editing, upscaling, or image-to-video, not a generative combiner that invents pixels between sources.',
    publishedPrefix: 'Published September 7, 2026 · Last updated',
    facts: [
      { label: 'Images', value: '2 to 9 images' },
      { label: 'Formats', value: 'JPG, PNG, and WebP' },
      { label: 'Layouts', value: 'Horizontal, vertical, or grid layouts' },
      { label: 'Processing', value: 'Local browser processing' },
    ],
  },
  definition: {
    eyebrow: 'Definition',
    description:
      'An image stitcher is a browser-based layout tool that arranges 2-9 JPG, PNG, or WebP images on one canvas. It creates horizontal rows, vertical strips, or grids while keeping every source in a separate cell and processing the files locally.',
    p1: 'Real output from this workflow is a joined canvas, not a blended scene. Three source photos can sit in equal 16:9 cells with a white gap and export as one image. That result is useful for comparisons, contact sheets, story panels, and long visual references.',
    p2: 'Creators often open an image stitcher after they already have stills from a camera roll, a Grok generation, or a screenshot sequence. The job is to keep each source identifiable while packing them into one file that can be shared, annotated, or sent into another Grok Imagine workflow.',
  },
  proof: {
    eyebrow: 'Output proof',
    title: 'See what the Image Stitcher creates',
    description:
      'The image stitcher does not synthesize a new scene. It creates one canvas, calculates a cell for each source, and draws the images in the selected order.',
    alt: 'Image stitcher output with three 16:9 frames in a Horizontal layout',
    figcaptionExtra:
      'Cover fills every cell and may crop an outer edge. Contain preserves the complete source and adds background around images whose shape differs from the selected cell ratio.',
  },
  capabilities: {
    eyebrow: 'Core capabilities',
    title: 'What can the Image Stitcher do?',
    description:
      'The image stitcher focuses on repeatable layout controls rather than generative editing, so the exported arrangement stays easy to predict.',
  },
  howTo: {
    eyebrow: 'Four-step workflow',
    title: 'How to stitch images online',
    description:
      'The live preview updates after every layout change, so you can check the complete image stitcher result before creating the download.',
  },
  layoutGuide: {
    eyebrow: 'Layout guide',
    title: 'Choose the right image layout',
    description:
      'Use the image stitcher layout guide to match the canvas to the way the finished image will be read.',
    caption: 'Horizontal, vertical, and grid layout choices',
    headers: {
      layout: 'Layout',
      input: 'Best input',
      output: 'Expected output',
      fit: 'Recommended fit',
    },
  },
  specs: {
    eyebrow: 'Technical specifications',
    title: 'Technical image stitching specifications',
    description:
      'These are the current input, layout, processing, and export limits enforced by the browser workbench.',
    caption: 'Image stitcher input and export specifications',
    headers: {
      parameter: 'Parameter',
      support: 'Current support',
    },
  },
  useCases: {
    eyebrow: 'Practical uses',
    title: 'Common image stitching use cases',
    description:
      'Use the same image stitcher controls for comparisons, long references, and compact visual collections.',
    startingInput: 'Starting input',
    toolAction: 'Tool action',
    expectedResult: 'Expected result',
  },
  limits: {
    eyebrow: 'Boundaries',
    title: 'Limits and quality checks',
    description:
      'Keep Cover, Contain, file size, and export format in view before you download. The workbench will not invent missing pixels or repair panorama seams.',
  },
  faq: {
    eyebrow: 'Questions and answers',
    title: 'Image Stitcher FAQ',
    description:
      'Direct answers about local processing, image quality, side-by-side layouts, and the difference between stitching and generative combining.',
  },
  nextSteps: {
    eyebrow: 'After export',
    title: 'Continue with the finished image',
    description:
      'After the image stitcher exports one canvas, keep the download on your device. Local files are not stored in History.',
    body: 'Download the canvas first, then open an adjacent workflow if you need motion or a server-processed pass. Review the privacy policy before moving from local stitching into any uploaded workflow.',
    ariaLabel: 'Continue with the finished image',
    videoLabel: 'Turn the stitched image into video',
    videoDetail:
      'Animate the finished still after you download the local canvas.',
    privacyLabel: 'Review the privacy policy',
    privacyDetail:
      'Check the policy before moving from local stitching into any server-processed workflow.',
  },
  cta: {
    eyebrow: 'Ready to arrange',
    title: 'Stitch your images now',
    body: 'Add two or more files to the image stitcher, choose a predictable layout, inspect the preview, and download one finished canvas.',
    button: 'Open the image stitcher',
  },
  schema: {
    softwareName: 'Image stitcher',
    actionName: 'Stitch images in the browser',
    howToName: 'How to stitch images online',
    howToDescription:
      'Add 2-9 images, choose a layout, adjust cell settings, then preview and download one canvas.',
    breadcrumbHome: 'Home',
    breadcrumbPage: 'Image stitcher',
    proofImageName: 'Image stitcher horizontal 16:9 proof',
  },
};

export const IMAGE_STITCH_UI: ImageStitchUi = {
  headerTitle: 'Image stitcher',
  sourceImages: 'Source images',
  addImages: 'Add images',
  addHint: '2-9 JPG, PNG, or WebP files, up to 20MB each',
  chooseFiles: 'Choose files',
  layout: 'Layout',
  horizontal: 'Horizontal',
  vertical: 'Vertical',
  grid: 'Grid',
  cellRatio: 'Cell ratio',
  imageFit: 'Image fit',
  contain: 'Contain',
  cover: 'Cover',
  gap: 'Gap',
  background: 'Background',
  format: 'Format',
  stitchDownload: 'Stitch & download',
  preparingDownload: 'Preparing download...',
  preview: 'Preview',
  addAtLeastTwo: 'Add at least two images',
  noPreview: 'No preview yet',
  noPreviewHint:
    'Your stitched result appears here after two images are ready.',
  waiting: 'Waiting for images',
  livePreviewNote:
    'Live preview is scaled to {edge}px. Download uses the full export canvas.',
  maxImagesError: 'You can add up to {count} images.',
  unsupportedTypeError: 'Use JPG, PNG, or WebP files.',
  fileTooLargeError: 'Each image must be 20MB or smaller.',
  readFailedError: 'The selected images could not be read.',
  exportFailedError: 'The stitched image could not be downloaded.',
  loadFailedError: '{name} could not be read as an image.',
  previewFailedError: 'Preview image failed.',
  moveEarlier: 'Move {name} earlier',
  moveLater: 'Move {name} later',
  remove: 'Remove {name}',
};

export const IMAGE_STITCH_DATA: ImageStitchSeoData = {
  path: IMAGE_STITCH_PATH,
  seoTitle: IMAGE_STITCH_SEO_TITLE,
  h1: IMAGE_STITCH_H1,
  definitionH2: IMAGE_STITCH_DEFINITION_H2,
  seoDescription: IMAGE_STITCH_SEO_DESCRIPTION,
  keywords: IMAGE_STITCH_KEYWORDS,
  datePublished: IMAGE_STITCH_DATE_PUBLISHED,
  lastUpdated: IMAGE_STITCH_LAST_UPDATED,
  lastUpdatedLabel: IMAGE_STITCH_LAST_UPDATED_LABEL,
  quickAnswer: IMAGE_STITCH_QUICK_ANSWER,
  proofImage: IMAGE_STITCH_PROOF_IMAGE,
  proofWidth: IMAGE_STITCH_PROOF_WIDTH,
  proofHeight: IMAGE_STITCH_PROOF_HEIGHT,
  proofCaption: IMAGE_STITCH_PROOF_CAPTION,
  proofCanvas: IMAGE_STITCH_PROOF_CANVAS,
  typicalTime: IMAGE_STITCH_TYPICAL_TIME,
  steps: IMAGE_STITCH_STEPS,
  features: IMAGE_STITCH_FEATURES,
  useCases: IMAGE_STITCH_USE_CASES,
  limits: IMAGE_STITCH_LIMITS,
  faqs: IMAGE_STITCH_FAQS,
  layoutGuide: IMAGE_STITCH_LAYOUT_GUIDE,
  specs: IMAGE_STITCH_SPECS,
  schemaFeatures: IMAGE_STITCH_SCHEMA_FEATURES,
  copy: IMAGE_STITCH_COPY,
  ui: IMAGE_STITCH_UI,
};

export function getImageStitchSeoData(
  locale: Locale = getLocale()
): ImageStitchSeoData {
  if (locale === 'es') return ES_IMAGE_STITCH_DATA;
  return IMAGE_STITCH_DATA;
}
